/**
 * @file PricingSetPopover.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-11-03
 * @description Pricing & Costing's "Set cost / sale / both for group…" (and for
 *              one line, or the ticked lines) — Cam, 19 Oct 2026: "Right click a
 *              cost or sale price for a product group within a customer group
 *              could have an option to set cost for group / set sale for group /
 *              set both for group, and then user can split the group and change
 *              some if they need to". A small popover by the pointer: per side,
 *              the options the lines have (each line its own of that kind) or
 *              one typed figure, prefilled with what the lines share (else the
 *              most common proposal). "Set" makes it each line's own choice on
 *              screen (nothing written); "Set and confirm" confirms them too,
 *              through the ordinary per-line confirm. Lines that can't take it
 *              are named. Rules: utils/pricingCostingView (groupSideOptions,
 *              setPrefill, sideSummary, differsText).
 *
 *              1.1.0 (2026-10-28, Cam — "Allow cost/ sale price figures to be
 *              applied per tonne or per other unit"): the typed figure is per
 *              the lines' unit, per T or per kg (PerFigureInput, utils/pricePer)
 *              — lines in different units start per tonne (tracker q03), the
 *              last switch remembered (`enterPer` / `onPerChange`); each line
 *              stores it per its own unit. Lines in different units or figures
 *              are shown normalised per tonne.
 *
 *              1.2.0 (2026-10-29, N9 — the redesign's callout E): options that
 *              give the same figure on every line are one option naming each
 *              source (groupSideOptions' `entries`); choosing it sets each line
 *              to its proposal when that is one of them, else the highest in
 *              its ladder.
 *
 *              1.3.0 (2026-11-03, Cam — "add the option to include a note when
 *              hand typing a cost or sale price"): a typed figure has an
 *              optional Note (PricingNoteInput), set on every line with it and
 *              sent with "Set and confirm"; prefilled with the note the lines
 *              share. Greyed before migrations/pricing_line_choices.sql
 *              (`notesReady`).
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSlideOutButton } from './SlideOutButton';
import { money, parsePrice } from '../utils/pricingCosting';
import { groupSideOptions, setPrefill, sideSummary, differsText, entryGroupChoice } from '../utils/pricingCostingView';
import { defaultPer, perOptions, unitsMixed, normalisedText, PER_UNIT } from '../utils/pricePer';
import PerFigureInput from './PerFigureInput';
import PricingNoteInput from './PricingNoteInput';

const PAD = 8;
const SIDE_NAME = { cost: 'Cost', sale: 'Sale price' };
// A line a side does not apply to is no news; only the reasons that are.
const quiet = (why) => /^No (cost|sale) on this line$/.test(why);

function PricingSetPopover({ title, rows, sides, per = '', x = 0, y = 0, readOnly = false, confirmBlock = {}, labelOf = (k) => k, onApply, onClose,
  enterPer = {}, onPerChange = () => {}, notesReady = false }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y, ready: false });
  const info = useMemo(() => Object.fromEntries(sides.map(side => [side, {
    g: groupSideOptions(rows, side),
    sum: sideSummary(rows, side),
    prefill: setPrefill(rows, side)
  }])), [rows, sides]);
  // What a typed figure is per: lines in different units start per tonne
  // (q03), else their own unit — as last switched (utils/pricePer).
  const mixed = unitsMixed(rows);
  const perOpts = useMemo(() => perOptions(rows), [rows]);
  const startPer = (side) => defaultPer(rows, { mixed: enterPer[`mixed_${side}`], single: enterPer[`single_${side}`] });
  const [pick, setPick] = useState(() => Object.fromEntries(sides.map(side => {
    const p = info[side].prefill;
    // The prefill is a figure per each line's own unit; per tonne it is
    // offered as one only when the lines are in one unit.
    const per0 = startPer(side);
    // The note the lines' typed figures share (1.3.0), else none.
    const notes = new Set(rows.filter(v => v[side]?.applies).map(v => (v[side].chosen?.id === 'manual' ? String(v[side].chosen.note || '') : '')));
    const note = notes.size === 1 ? [...notes][0] : '';
    return [side, { ...p, per: per0, value: p.mode === 'manual' && per0 !== PER_UNIT ? '' : p.value, note }];
  })));
  useSlideOutButton([sides.join(','), readOnly]);

  useLayoutEffect(() => {
    const el = ref.current;
    const w = el ? el.offsetWidth : 0;
    const h = el ? el.offsetHeight : 0;
    setPos({
      left: Math.max(PAD, Math.min(x, window.innerWidth - w - PAD)),
      top: Math.max(PAD, Math.min(y, window.innerHeight - h - PAD)),
      ready: true
    });
  }, [x, y]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    // The choice it opens on: a typed figure → its box, an option → that radio.
    const el = ref.current;
    (el?.querySelector('label.on input[type="text"]:not(:disabled)')
      || el?.querySelector('input[type="radio"]:checked:not(:disabled)'))?.focus();
  }, []);

  const live = sides.filter(side => info[side].g.takers.length > 0);
  const problem = (side) => {
    const p = pick[side];
    if (p.mode !== 'manual') return null;
    return parsePrice(p.value) ? null : `Type a ${side === 'cost' ? 'cost' : 'price'} above £0`;
  };
  const problems = live.map(problem).filter(Boolean);
  const canSet = !readOnly && live.length > 0 && !problems.length;
  const confirmWhy = live.map(side => confirmBlock[side]).filter(Boolean)[0] || null;
  // A mode is an entry's key (1.2.0): one source, or several giving one figure.
  const choiceOf = (side) => (pick[side].mode === 'manual'
    ? { id: 'manual', value: String(parsePrice(pick[side].value)), per: pick[side].per || PER_UNIT,
      ...(notesReady && pick[side].note ? { note: pick[side].note } : {}) }
    : entryGroupChoice(info[side].g.entries.find(en => en.key === pick[side].mode) || { id: pick[side].mode }));
  const apply = (confirm) => {
    if (!canSet || (confirm && confirmWhy)) return;
    onApply(Object.fromEntries(live.map(side => [side, choiceOf(side)])), confirm);
  };

  return createPortal(
    <div className="pc-pop-overlay" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onContextMenu={(e) => { e.preventDefault(); onClose(); }}>
      <div ref={ref} role="dialog" aria-label={title} className={`pc-pop${pos.ready ? ' ready' : ''}`}
        style={{ left: pos.left, top: pos.top }}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') { e.preventDefault(); apply(false); } }}>
        <h4>{title}</h4>
        {sides.map(side => {
          const { g, sum } = info[side];
          const p = pick[side];
          const differs = differsText(sum, side);
          const loud = g.blocked.filter(b => !quiet(b.why));
          const name = `pc-pop-${side}`;
          const setMode = (mode) => setPick(prev => ({ ...prev, [side]: { ...prev[side], mode } }));
          return (
            <div key={side} className="pc-pop-side">
              <h5><span className="material-symbols-rounded">{side === 'cost' ? 'shopping_cart' : 'sell'}</span>{SIDE_NAME[side]}
                {g.takers.length > 0 && <span className="pc-muted">· {g.takers.length === rows.length ? `all ${rows.length} line${rows.length === 1 ? '' : 's'}` : `${g.takers.length} of ${rows.length} lines`}</span>}
              </h5>
              {!g.takers.length ? (
                <p className="pc-muted">{loud.length ? `Can't be set here: ${loud[0].why}` : `No ${side === 'cost' ? 'cost' : 'sale price'} on ${rows.length === 1 ? 'this line' : 'these lines'}.`}</p>
              ) : (
                <>
                  <p className="pc-muted">Now: {sum.min === null ? 'none yet' : sum.same ? `${money(sum.min)}${per}${sum.none ? `, ${sum.none} with none` : ''}` : `${money(sum.min)}–${money(sum.max)}${per}`}{differs ? ` · ${differs}` : ''}</p>
                  {normalisedText(rows, side, (r) => labelOf(r.key)) && (
                    <p className="pc-muted pc-per-note">Per tonne: {normalisedText(rows, side, (r) => labelOf(r.key))}</p>
                  )}
                  {g.entries.map(o => (
                    <label key={o.key} className={`pc-option ${p.mode === o.key ? 'on' : ''}${o.merged ? ' pc-option-agree' : ''}`}>
                      <input type="radio" name={name} checked={p.mode === o.key} disabled={readOnly} onChange={() => setMode(o.key)} />
                      <span className="pc-option-text">
                        <span className="l1">{o.label}</span>
                        <span className="l2">{o.all ? (g.takers.length === 1 ? 'this line' : 'every line') : `${o.lines} of ${g.takers.length} lines`}{o.same ? '' : ' · each line its own'}
                          {o.merged && <> · <span className="pc-agree"><span className="material-symbols-rounded" aria-hidden="true">done_all</span>{o.sources.length} sources agree</span></>}</span>
                      </span>
                      <span className="pc-option-val">{o.min === null ? '—' : o.same ? money(o.min) : `${money(o.min)}–${money(o.max)}`}<span className="pc-unit">{per}</span></span>
                    </label>
                  ))}
                  <label className={`pc-option ${p.mode === 'manual' ? 'on' : ''}`}>
                    <input type="radio" name={name} checked={p.mode === 'manual'} disabled={readOnly} onChange={() => setMode('manual')} />
                    <span className="pc-option-text">
                      <span className="l1">{g.takers.length === 1 ? `Type a ${side === 'cost' ? 'cost' : 'price'}` : `One ${side === 'cost' ? 'cost' : 'price'} for them all`}</span>
                      <span className="l2">Sends {g.takers.length === 1 ? 'it' : 'them'} to Check</span>
                    </span>
                    <span onFocus={() => setMode('manual')}>
                      <PerFigureInput text={p.value} per={p.per || PER_UNIT} options={perOpts} disabled={readOnly}
                        ariaLabel={`${SIDE_NAME[side]} to set`}
                        eq={(p.per || PER_UNIT) !== PER_UNIT ? 'each line in its own unit' : ''}
                        onText={(value) => setPick(prev => ({ ...prev, [side]: { ...prev[side], mode: 'manual', value } }))}
                        onPer={(nextPer) => { onPerChange(mixed, nextPer, side); setPick(prev => ({ ...prev, [side]: { ...prev[side], mode: 'manual', per: nextPer } })); }} />
                    </span>
                  </label>
                  {p.mode === 'manual' && (
                    <div className="pc-option-note">
                      <PricingNoteInput value={p.note || ''} disabled={readOnly} notReady={!notesReady}
                        ariaLabel={`Note for the ${side === 'cost' ? 'cost' : 'price'} to set`}
                        onChange={(note) => setPick(prev => ({ ...prev, [side]: { ...prev[side], note } }))} />
                    </div>
                  )}
                  {loud.length > 0 && (
                    <div className="pc-note"><span className="material-symbols-rounded">info</span>
                      <span>Not changed: {loud.slice(0, 5).map(b => `${labelOf(b.key)} — ${b.why}`).join('; ')}{loud.length > 5 ? ` and ${loud.length - 5} more` : ''}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
        <p className="pc-muted pc-pop-foot">Set makes it each line's own choice — nothing is written until confirmed. Edit a line on its own afterwards and the group shows "Differs".</p>
        <div className="buttons slideout pc-pop-buttons">
          <div><button type="button" className="btn-slideout" onClick={onClose}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
            <span className="btn-label">Cancel</span>
          </button></div>
          <div><button type="button" className="btn-slideout" disabled={!canSet} onClick={() => apply(false)}
            title={problems[0] || (!live.length ? 'Nothing here can be set' : 'Set it on the lines — confirm later')}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">edit</span></div>
            <span className="btn-label">Set</span>
          </button></div>
          <div><button type="button" className="btn-slideout" disabled={!canSet || !!confirmWhy} onClick={() => apply(true)}
            title={problems[0] || confirmWhy || 'Set it and confirm each line (one Undo puts them all back)'}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">done_all</span></div>
            <span className="btn-label">Set and confirm</span>
          </button></div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PricingSetPopover;
